import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Copy, ExternalLink } from 'lucide-react';
import { apiService, DashboardEmbedInfo, DashboardSite, DashboardUser } from '@/api/apiService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const DashboardAdminPage = () => {
  const [isAllowed, setIsAllowed] = useState(false);
  const [sites, setSites] = useState<DashboardSite[]>([]);
  const [users, setUsers] = useState<DashboardUser[]>([]);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'owner' | 'editor' | 'viewer'>('viewer');
  const [inviteSiteId, setInviteSiteId] = useState('');
  const [inviteHours, setInviteHours] = useState('72');
  const [lastInviteToken, setLastInviteToken] = useState('');
  const [resetTokens, setResetTokens] = useState<Record<number, string>>({});

  const [membershipUserId, setMembershipUserId] = useState('');
  const [membershipSiteId, setMembershipSiteId] = useState('');
  const [membershipRole, setMembershipRole] = useState<'owner' | 'editor' | 'viewer'>('viewer');
  const [embedSiteId, setEmbedSiteId] = useState('');
  const [embedInfo, setEmbedInfo] = useState<DashboardEmbedInfo | null>(null);
  const [customScript, setCustomScript] = useState('');
  const [customIframe, setCustomIframe] = useState('');
  const [isSavingEmbed, setIsSavingEmbed] = useState(false);

  const extractApiErrorMessage = (error: any, fallback: string) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const firstMessage = detail.find((item) => typeof item?.msg === 'string')?.msg;
      if (firstMessage) return firstMessage;
    }
    return fallback;
  };

  const load = async () => {
    const me = await apiService.dashboardMe();
    if (!me.user?.is_platform_admin) {
      setIsAllowed(false);
      return;
    }
    setIsAllowed(true);
    const [usersResp, siteRows] = await Promise.all([
      apiService.dashboardListUsers(),
      apiService.dashboardListSites(),
    ]);
    setUsers(usersResp.users || []);
    setSites(siteRows || []);
    const firstSiteId = siteRows?.[0]?.id;
    if (firstSiteId) {
      setEmbedSiteId(String(firstSiteId));
    }
  };

  useEffect(() => {
    load().catch((err) => {
      console.error(err);
      setIsAllowed(false);
    });
  }, []);

  useEffect(() => {
    if (!embedSiteId) {
      setEmbedInfo(null);
      setCustomScript('');
      setCustomIframe('');
      return;
    }
    apiService.dashboardGetEmbedInfo(Number(embedSiteId))
      .then((info) => {
        setEmbedInfo(info);
        setCustomScript(info.script_snippet_custom || '');
        setCustomIframe(info.iframe_snippet_custom || '');
      })
      .catch((error) => {
        console.error(error);
        toast.error(extractApiErrorMessage(error, 'Failed to load embed settings'));
      });
  }, [embedSiteId]);

  const copyText = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  const handleSaveEmbed = async (e: FormEvent) => {
    e.preventDefault();
    if (!embedSiteId) return;
    try {
      setIsSavingEmbed(true);
      const updated = await apiService.dashboardUpdateEmbedInfo(Number(embedSiteId), {
        script_snippet: customScript,
        iframe_snippet: customIframe,
      });
      setEmbedInfo(updated);
      setCustomScript(updated.script_snippet_custom || '');
      setCustomIframe(updated.iframe_snippet_custom || '');
      toast.success('Embed settings saved');
    } catch (error: any) {
      toast.error(extractApiErrorMessage(error, 'Failed to save embed settings'));
    } finally {
      setIsSavingEmbed(false);
    }
  };

  const handleResetEmbed = async () => {
    if (!embedSiteId) return;
    try {
      setIsSavingEmbed(true);
      const updated = await apiService.dashboardUpdateEmbedInfo(Number(embedSiteId), {
        clear_custom: true,
      });
      setEmbedInfo(updated);
      setCustomScript('');
      setCustomIframe('');
      toast.success('Custom embed cleared');
    } catch (error: any) {
      toast.error(extractApiErrorMessage(error, 'Failed to reset embed settings'));
    } finally {
      setIsSavingEmbed(false);
    }
  };

  const handleCreateInvite = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const parsedHours = Number.parseInt(inviteHours, 10);
      if (!Number.isFinite(parsedHours) || parsedHours < 1) {
        toast.error('Expiry hours must be a whole number >= 1');
        return;
      }
      const parsedSiteId = inviteSiteId.trim() ? Number.parseInt(inviteSiteId, 10) : undefined;
      if (
        inviteSiteId.trim() &&
        (typeof parsedSiteId !== 'number' || !Number.isFinite(parsedSiteId) || parsedSiteId < 1)
      ) {
        toast.error('Site ID must be a whole number >= 1');
        return;
      }

      const response = await apiService.dashboardCreateInvite(
        inviteEmail.trim(),
        parsedSiteId,
        inviteRole,
        parsedHours
      );
      setLastInviteToken(response?.invite_token || '');
      toast.success('Invite created');
      setInviteEmail('');
    } catch (error: any) {
      toast.error(extractApiErrorMessage(error, 'Failed to create invite'));
    }
  };

  const handleUpsertMembership = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const parsedUserId = Number.parseInt(membershipUserId, 10);
      const parsedSiteId = Number.parseInt(membershipSiteId, 10);
      if (!Number.isFinite(parsedUserId) || parsedUserId < 1) {
        toast.error('User ID must be a whole number >= 1');
        return;
      }
      if (!Number.isFinite(parsedSiteId) || parsedSiteId < 1) {
        toast.error('Site ID must be a whole number >= 1');
        return;
      }

      await apiService.dashboardUpsertMembership(
        parsedUserId,
        parsedSiteId,
        membershipRole
      );
      toast.success('Membership updated');
    } catch (error: any) {
      toast.error(extractApiErrorMessage(error, 'Failed to update membership'));
    }
  };

  if (!isAllowed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Admin Access Required</CardTitle>
          <CardDescription>Sign in as a platform admin to access this page.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Platform Admin Panel</h1>
        <Button asChild variant="outline">
          <Link to="/dashboard">Tenant dashboard</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Client Embed Overrides</CardTitle>
          <CardDescription>Store custom script/iframe snippets per client site and update them anytime.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Client site</Label>
              <select
                className="w-full rounded-md border border-input bg-background p-2 text-foreground"
                value={embedSiteId}
                onChange={(e) => setEmbedSiteId(e.target.value)}
              >
                <option value="">Select site</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.id} - {site.name}
                  </option>
                ))}
              </select>
            </div>
            {embedInfo ? (
              <div className="space-y-2">
                <Label>Test page</Label>
                <Button asChild variant="outline" className="w-full justify-start">
                  <a href={embedInfo.chat_ui_test_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {embedInfo.chat_ui_test_url}
                  </a>
                </Button>
              </div>
            ) : null}
          </div>

          {embedInfo ? (
            <form className="space-y-4" onSubmit={handleSaveEmbed}>
              <div className="space-y-2">
                <Label>Effective script embed (current)</Label>
                <div className="flex gap-2">
                  <Textarea rows={3} className="font-mono text-xs" value={embedInfo.script_snippet} readOnly />
                  <Button type="button" variant="outline" size="icon" onClick={() => copyText('Script embed', embedInfo.script_snippet)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Effective iframe embed (current)</Label>
                <div className="flex gap-2">
                  <Textarea rows={3} className="font-mono text-xs" value={embedInfo.iframe_snippet} readOnly />
                  <Button type="button" variant="outline" size="icon" onClick={() => copyText('Iframe embed', embedInfo.iframe_snippet)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Custom script override (stored per client)</Label>
                <Textarea
                  rows={5}
                  className="font-mono text-xs"
                  value={customScript}
                  onChange={(e) => setCustomScript(e.target.value)}
                  placeholder={embedInfo.script_snippet_default}
                />
              </div>
              <div className="space-y-2">
                <Label>Custom iframe override (stored per client)</Label>
                <Textarea
                  rows={5}
                  className="font-mono text-xs"
                  value={customIframe}
                  onChange={(e) => setCustomIframe(e.target.value)}
                  placeholder={embedInfo.iframe_snippet_default}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={isSavingEmbed}>
                  {isSavingEmbed ? 'Saving...' : 'Save embed overrides'}
                </Button>
                <Button type="button" variant="outline" disabled={isSavingEmbed} onClick={handleResetEmbed}>
                  Reset to generated defaults
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-sm text-muted-foreground">Select a site to manage embed snippets.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create Invite</CardTitle>
          <CardDescription>Invite-only onboarding with optional site role binding.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={handleCreateInvite}>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" required />
            </div>
            <div className="space-y-2">
              <Label>Site ID (optional)</Label>
              <Input value={inviteSiteId} onChange={(e) => setInviteSiteId(e.target.value)} type="number" min="1" step="1" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <select
                className="w-full rounded-md border border-input bg-background p-2 text-foreground"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'owner' | 'editor' | 'viewer')}
              >
                <option value="owner">owner</option>
                <option value="editor">editor</option>
                <option value="viewer">viewer</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Expiry hours</Label>
              <Input value={inviteHours} onChange={(e) => setInviteHours(e.target.value)} type="number" min="1" step="1" />
            </div>
            <Button type="submit">Create invite</Button>
          </form>
          {lastInviteToken ? (
            <p className="mt-4 text-sm text-amber-500">
              Share this one-time token securely: <code>{lastInviteToken}</code>
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assign Membership</CardTitle>
          <CardDescription>Grant or update per-site owner/editor/viewer role.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid grid-cols-1 gap-4 md:grid-cols-3" onSubmit={handleUpsertMembership}>
            <div className="space-y-2">
              <Label>User ID</Label>
              <Input value={membershipUserId} onChange={(e) => setMembershipUserId(e.target.value)} type="number" min="1" step="1" required />
            </div>
            <div className="space-y-2">
              <Label>Site ID</Label>
              <Input value={membershipSiteId} onChange={(e) => setMembershipSiteId(e.target.value)} type="number" min="1" step="1" required />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <select
                className="w-full rounded-md border border-input bg-background p-2 text-foreground"
                value={membershipRole}
                onChange={(e) => setMembershipRole(e.target.value as 'owner' | 'editor' | 'viewer')}
              >
                <option value="owner">owner</option>
                <option value="editor">editor</option>
                <option value="viewer">viewer</option>
              </select>
            </div>
            <Button type="submit">Save membership</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>Recent dashboard users. Use "Reset password" to generate a one-time token the user can redeem on the login page.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {users.map((user) => (
              <div key={user.id} className="rounded border p-3 text-sm space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-medium">{user.email}</div>
                    <div className="text-muted-foreground">
                      id: {user.id} | admin: {String(user.is_platform_admin)} | reset required: {String(user.must_reset_password)}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const result = await apiService.dashboardGenerateResetToken(Number(user.id));
                        setResetTokens((prev) => ({ ...prev, [Number(user.id)]: result.reset_token }));
                        toast.success('Reset token generated — share it with the user securely');
                      } catch {
                        toast.error('Failed to generate reset token');
                      }
                    }}
                  >
                    Reset password
                  </Button>
                </div>
                {resetTokens[Number(user.id)] && (
                  <div className="flex items-center gap-2 rounded bg-muted/50 px-3 py-2">
                    <code className="flex-1 text-xs break-all">{resetTokens[Number(user.id)]}</code>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(resetTokens[Number(user.id)]);
                        toast.success('Token copied');
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sites</CardTitle>
          <CardDescription>Accessible sites for this admin account.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {sites.map((site) => (
              <div key={site.id} className="rounded border p-3 text-sm">
                {site.id} - {site.name} ({site.role})
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardAdminPage;
